import React, { useState, memo } from 'react';
import { Button, Typography, Modal, Input, Checkbox, Divider, App, message } from 'antd';
import { UnlockOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { Invoice } from '../../services/paymentService';

const { Text } = Typography;

export interface WorkOrderActionsProps {
  invoice: Invoice | null;
  forceUnlockMode: boolean;
  isOwner: boolean;
  invoiceNumber?: string;
  woNumber?: string;
  pendingItemCount: number;
  pricingTotal: number;
  unapproveOpen: boolean;
  bulkApprovalOpen: boolean;
  onForceUnlock: () => void;
  onForceDelete: () => Promise<void>;
  onRelockWorkOrder: () => void;
  onUnapprove: (reason: string) => Promise<void>;
  onBulkApproval: (staffName: string, notes: string, confirmed: boolean) => Promise<void>;
  onUnapproveClose: () => void;
  onBulkApprovalClose: () => void;
}

const WorkOrderActions: React.FC<WorkOrderActionsProps> = ({
  invoice, forceUnlockMode, isOwner, invoiceNumber, woNumber,
  pendingItemCount, pricingTotal, unapproveOpen, bulkApprovalOpen,
  onForceUnlock, onForceDelete, onRelockWorkOrder, onUnapprove, onBulkApproval,
  onUnapproveClose, onBulkApprovalClose,
}) => {
  const { modal: modalApi } = App.useApp();
  const [unapproveReason, setUnapproveReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [bulkStaff, setBulkStaff] = useState('');
  const [bulkNotes, setBulkNotes] = useState('');
  const [bulkConfirmed, setBulkConfirmed] = useState(false);

  const handleUnapprove = async () => {
    if (!unapproveReason || unapproveReason.trim().split(/\s+/).length < 2) return;
    setSubmitting(true);
    try { await onUnapprove(unapproveReason); } finally { setSubmitting(false); onUnapproveClose(); setUnapproveReason(''); }
  };

  const handleBulkApproval = async () => {
    await onBulkApproval(bulkStaff, bulkNotes, bulkConfirmed);
    onBulkApprovalClose(); setBulkStaff(''); setBulkNotes(''); setBulkConfirmed(false);
  };

  const confirmForceUnlock = () => {
    modalApi.confirm({
      title: 'Force Unlock Work Order',
      icon: <ExclamationCircleOutlined style={{ color: '#f97316' }} />,
      centered: true,
      content: (<div><p>This will temporarily remove the lock and allow you to edit this work order.</p><p style={{ marginTop: 8, color: '#dc2626', fontWeight: 500 }}>WARNING: The invoice {invoiceNumber} will remain unchanged. Any edits made will NOT be reflected in the already-generated invoice.</p><p style={{ marginTop: 8 }}>Use this feature only to correct critical errors or add missing information.</p></div>),
      okText: 'Yes, Unlock Temporarily', okButtonProps: { danger: true }, cancelText: 'Cancel',
      onOk: onForceUnlock,
    });
  };

  const confirmForceDelete = () => {
    modalApi.confirm({
      title: 'Force Delete Work Order',
      icon: <ExclamationCircleOutlined style={{ color: '#dc2626' }} />,
      centered: true,
      content: (<div><p style={{ color: '#dc2626', fontWeight: 600, fontSize: 16 }}>DANGEROUS OPERATION</p><p style={{ marginTop: 8 }}>This will PERMANENTLY delete:</p><ul style={{ marginTop: 8, marginLeft: 20 }}><li>Work Order {woNumber}</li><li>Invoice {invoiceNumber}</li><li>All line items and parts</li><li>All payment history</li></ul><p style={{ marginTop: 12, fontWeight: 500 }}>This action CANNOT be undone!</p></div>),
      okText: 'Yes, Delete Permanently', okButtonProps: { danger: true, type: 'primary' }, cancelText: 'Cancel',
      onOk: onForceDelete,
    });
  };

  return (
    <>
      {invoice && !forceUnlockMode && (
        <div style={{ backgroundColor: '#fff7ed', borderBottom: '2px solid #fb923c', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
          <Text style={{ color: '#c2410c', fontWeight: 500, fontSize: 13 }}>Work Order Locked - Invoice {invoiceNumber} has been generated. No further edits allowed.</Text>
          {isOwner && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Button size="small" danger icon={<UnlockOutlined />} onClick={confirmForceUnlock} style={{ fontSize: 12, fontWeight: 500 }}>Force Unlock</Button>
              <Button size="small" danger type="primary" icon={<DeleteOutlined />} onClick={confirmForceDelete} style={{ fontSize: 12, fontWeight: 500 }}>Force Delete</Button>
            </div>
          )}
        </div>
      )}
      {forceUnlockMode && invoice && (
        <div style={{ backgroundColor: '#fef2f2', borderBottom: '2px solid #dc2626', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
          <Text style={{ color: '#dc2626', fontWeight: 600, fontSize: 13 }}>FORCE UNLOCK MODE - Invoice {invoiceNumber} will NOT be updated with your changes!</Text>
          <Button size="small" onClick={onRelockWorkOrder} style={{ fontSize: 12, fontWeight: 500 }}>Re-Lock Work Order</Button>
        </div>
      )}
      <Modal title="Unapprove Work Order" open={unapproveOpen} onOk={handleUnapprove} centered
        onCancel={() => { onUnapproveClose(); setUnapproveReason(''); }}
        okText="Unapprove & Delete" okButtonProps={{ danger: true, loading: submitting }} cancelButtonProps={{ disabled: submitting }}>
        <div className="flex flex-col gap-4">
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            <p className="font-semibold mb-1">Warning: Destructive Action</p>
            <ul className="list-disc list-inside space-y-1">
              <li>This Work Order will be permanently deleted.</li>
              <li>Any associated appointments will be removed.</li>
              <li>The original Estimate will be reset to <strong>Unapproved</strong> status.</li>
            </ul>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason for Unapproval <span className="text-red-500">*</span></label>
            <Input.TextArea rows={3} placeholder="e.g., Customer requested changes to the estimate..." value={unapproveReason} onChange={(e) => setUnapproveReason(e.target.value)} />
            <p className="text-xs text-gray-500 mt-1">Minimum 2 words required.</p>
          </div>
        </div>
      </Modal>
      <Modal title="Approve All Items (In-Person)" open={bulkApprovalOpen} centered
        onCancel={() => { onBulkApprovalClose(); setBulkConfirmed(false); }}
        onOk={handleBulkApproval} okText="Confirm Approval" okButtonProps={{ disabled: !bulkConfirmed || !bulkStaff.trim() }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div><strong>Work Order #{woNumber}</strong></div>
          <div><strong>Pending items:</strong> {pendingItemCount}</div>
          <div><strong>Total:</strong> ${pricingTotal.toFixed(2)}</div>
          <Divider style={{ margin: '8px 0' }} />
          <div><div style={{ marginBottom: 4 }}>Approved By (Staff Name):</div><Input value={bulkStaff} onChange={(e) => setBulkStaff(e.target.value)} placeholder="Enter your name" /></div>
          <div><div style={{ marginBottom: 4 }}>Notes (optional):</div><Input.TextArea value={bulkNotes} onChange={(e) => setBulkNotes(e.target.value)} placeholder="e.g., customer at counter" rows={2} /></div>
          <Checkbox checked={bulkConfirmed} onChange={(e) => setBulkConfirmed(e.target.checked)}>Customer verbally confirmed approval for all items</Checkbox>
        </div>
      </Modal>
    </>
  );
};

export default memo(WorkOrderActions);
